import React, { useState, useEffect } from 'react';
import { Offcanvas, Tab, Tabs, Badge, Button } from 'react-bootstrap';
import { useDiagramStore } from '../store/diagramStore';
import { ServiceData, ActorData, ConnectorData, NetworkZone } from '../types/diagram.types';
import { CONNECTOR_STYLES } from '../constants/connectorStyles';
import { ZONE_COLORS } from '../constants/groupColors';

const ZONE_OPTIONS: { value: NetworkZone; label: string }[] = [
  { value: 'zone-external', label: 'External (Z1)' },
  { value: 'zone-dmz',      label: 'DMZ (Z2)' },
  { value: 'zone-kafka',    label: 'Kafka (Z5)' },
  { value: 'zone-internal', label: 'Internal (Z3)' },
  { value: 'zone-partners', label: 'Partners (Z4)' },
];

const ACTOR_TYPE_OPTIONS = [
  { value: 'client',       label: 'Client' },
  { value: 'lp',           label: 'LP (Liquidity Provider)' },
  { value: 'psp',          label: 'PSP' },
  { value: 'blockchain',   label: 'Blockchain' },
  { value: 'notification', label: 'Notification Service' },
  { value: 'compliance',   label: 'Compliance' },
];

const fieldStyle: React.CSSProperties = {
  width: '100%', fontSize: 12, border: '1px solid #E0E0E0',
  borderRadius: 4, padding: '5px 8px', outline: 'none',
  background: '#fff', boxSizing: 'border-box',
};

interface RightPanelProps {
  onEdit: (nodeId: string) => void;
}

const ZONE_LABELS: Record<NetworkZone, string> = {
  'zone-external': 'External',
  'zone-dmz': 'DMZ',
  'zone-kafka': 'Kafka (Z5)',
  'zone-internal': 'Internal',
  'zone-partners': 'Partners',
};

const RightPanel: React.FC<RightPanelProps> = ({ onEdit }) => {
  const { selectedNodeId, selectNode, nodes, edges, flows, softDeleteService, updateService } = useDiagramStore();
  const [activeTab, setActiveTab] = useState('info');
  const [isActorEditMode, setIsActorEditMode] = useState(false);
  const [actorEditName, setActorEditName] = useState('');
  const [actorEditZone, setActorEditZone] = useState<NetworkZone>('zone-partners');
  const [actorEditType, setActorEditType] = useState('');
  const [actorEditUrl, setActorEditUrl] = useState('');

  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const show = !!selectedNode && selectedNode.type !== 'zone';

  const data = selectedNode?.data as (ServiceData | ActorData);
  const isService = selectedNode?.type === 'service';
  const isActor = selectedNode?.type === 'actor';
  const svcData = isService ? (data as ServiceData) : null;
  const actorData = isActor ? (data as ActorData) : null;

  useEffect(() => {
    setIsActorEditMode(false);
    if (actorData) {
      setActorEditName(actorData.name ?? '');
      setActorEditZone(actorData.zone ?? 'zone-partners');
      setActorEditType(actorData.actorType ?? 'client');
      setActorEditUrl(actorData.url ?? '');
    }
  }, [selectedNodeId]);

  if (!show) return null;

  // Edges for this node
  const allMyEdges = edges.filter(e => e.source === selectedNodeId || e.target === selectedNodeId);
  const inboundEdges = allMyEdges.filter(e => e.target === selectedNodeId && (e.data as ConnectorData)?.type !== 'Kafka');
  const outboundEdges = allMyEdges.filter(e => e.source === selectedNodeId && (e.data as ConnectorData)?.type !== 'Kafka');

  // Kafka queues
  const kafkaPublish = allMyEdges.filter(e => e.source === selectedNodeId && (e.data as ConnectorData)?.type === 'Kafka');
  const kafkaSubscribe = allMyEdges.filter(e => e.target === selectedNodeId && (e.data as ConnectorData)?.type === 'Kafka');
  const kafkaTotal = kafkaPublish.length + kafkaSubscribe.length;

  // Flows that include this node's edges
  const nodeEdgeIds = allMyEdges.map(e => e.id);
  const nodeFlows = flows.filter(f =>
    f.edgeIds.some(eid => nodeEdgeIds.includes(eid))
  );

  const zone = (data as ServiceData)?.zone as NetworkZone | undefined;
  const zoneStyle = zone ? ZONE_COLORS[zone] : null;

  return (
    <Offcanvas
      show={show}
      onHide={() => selectNode(null)}
      placement="end"
      style={{ width: 380 }}
      backdrop={false}
      scroll
    >
      <Offcanvas.Header
        style={{
          background: '#F5F5F5',
          borderBottom: '1px solid #E0E0E0',
          padding: '12px 16px',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>
            {(data as ServiceData).name || (data as ActorData).name}
          </div>
          {zone && zoneStyle && (
            <Badge
              style={{
                background: zoneStyle.border,
                fontSize: 10,
                marginTop: 3,
              }}
            >
              {ZONE_LABELS[zone]}
            </Badge>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {isService && (
            <>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => onEdit(selectedNodeId!)}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => {
                  softDeleteService(selectedNodeId!);
                  selectNode(null);
                }}
              >
                Disable
              </Button>
            </>
          )}
          {isActor && !isActorEditMode && (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setIsActorEditMode(true)}
            >
              Edit
            </Button>
          )}
          <Button size="sm" variant="outline-secondary" onClick={() => selectNode(null)}>
            ✕
          </Button>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ padding: 0 }}>
        <Tabs
          activeKey={activeTab}
          onSelect={k => setActiveTab(k ?? 'info')}
          style={{ padding: '0 16px', borderBottom: '1px solid #E0E0E0' }}
          className="mb-0"
        >
          <Tab eventKey="info" title="Info" />
          <Tab eventKey="connectors" title={`Connectors (${inboundEdges.length + outboundEdges.length})`} />
          {kafkaTotal > 0 && <Tab eventKey="kafka" title={`Kafka (${kafkaTotal})`} />}
          <Tab eventKey="flows" title={`Flows (${nodeFlows.length})`} />
          {isService && <Tab eventKey="docs" title="Docs" />}
        </Tabs>

        <div style={{ padding: '16px', overflowY: 'auto', height: 'calc(100% - 100px)' }}>
          {/* INFO TAB */}
          {activeTab === 'info' && (
            <div>
              {svcData ? (
                <>
                  <InfoRow label="Group" value={svcData.group} />
                  <InfoRow label="Status">
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        background:
                          svcData.status === 'active' ? '#E8F5E9'
                          : svcData.status === 'planned' ? '#E3F2FD'
                          : '#F5F5F5',
                        color:
                          svcData.status === 'active' ? '#2E7D32'
                          : svcData.status === 'planned' ? '#1565C0'
                          : '#757575',
                      }}
                    >
                      {svcData.status}
                    </span>
                  </InfoRow>
                  {svcData.version && <InfoRow label="Version" value={svcData.version} />}
                  {svcData.repository && (
                    <InfoRow label="Repository">
                      <code style={{ fontSize: 11 }}>{svcData.repository}</code>
                    </InfoRow>
                  )}
                  {svcData.techStack?.length > 0 && (
                    <InfoRow label="Tech Stack">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {svcData.techStack.map(t => (
                          <span
                            key={t}
                            style={{
                              padding: '2px 7px',
                              borderRadius: 4,
                              fontSize: 11,
                              background: '#EEE',
                              color: '#424242',
                            }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </InfoRow>
                  )}
                  {svcData.databases?.length > 0 && (
                    <InfoRow label="Databases">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {svcData.databases.map(db => (
                          <span
                            key={db}
                            style={{
                              padding: '2px 7px',
                              borderRadius: 4,
                              fontSize: 11,
                              background: '#E3F2FD',
                              color: '#1565C0',
                            }}
                          >
                            {db}
                          </span>
                        ))}
                      </div>
                    </InfoRow>
                  )}
                  {svcData.description && (
                    <div style={{ marginTop: 12, fontSize: 12, color: '#616161', lineHeight: 1.5 }}>
                      {svcData.description}
                    </div>
                  )}
                </>
              ) : actorData && isActorEditMode ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9E9E9E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Name</div>
                    <input
                      style={fieldStyle}
                      value={actorEditName}
                      onChange={e => setActorEditName(e.target.value)}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9E9E9E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Zone</div>
                    <select
                      style={fieldStyle}
                      value={actorEditZone}
                      onChange={e => setActorEditZone(e.target.value as NetworkZone)}
                    >
                      {ZONE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9E9E9E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Type</div>
                    <select
                      style={fieldStyle}
                      value={actorEditType}
                      onChange={e => setActorEditType(e.target.value)}
                    >
                      {ACTOR_TYPE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9E9E9E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>URL</div>
                    <input
                      style={fieldStyle}
                      value={actorEditUrl}
                      onChange={e => setActorEditUrl(e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      size="sm"
                      variant="primary"
                      style={{ flex: 1 }}
                      onClick={() => {
                        updateService(selectedNodeId!, {
                          name: actorEditName,
                          label: actorEditName,
                          zone: actorEditZone,
                          actorType: actorEditType,
                          url: actorEditUrl,
                        } as Partial<ServiceData>);
                        setIsActorEditMode(false);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      style={{ flex: 1 }}
                      onClick={() => setIsActorEditMode(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : actorData ? (
                <>
                  <InfoRow label="Type" value={actorData.actorType} />
                  <InfoRow label="Zone" value={ZONE_LABELS[actorData.zone] ?? actorData.zone} />
                  {actorData.url && (
                    <InfoRow label="URL" value={actorData.url} />
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* CONNECTORS TAB */}
          {activeTab === 'connectors' && (
            <div>
              {inboundEdges.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#757575', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    ← Inbound ({inboundEdges.length})
                  </div>
                  {inboundEdges.map(e => <ConnectorRow key={e.id} edge={e} nodes={nodes} direction="in" />)}
                </>
              )}
              {outboundEdges.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#757575', margin: '12px 0 8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    → Outbound ({outboundEdges.length})
                  </div>
                  {outboundEdges.map(e => <ConnectorRow key={e.id} edge={e} nodes={nodes} direction="out" />)}
                </>
              )}
              {inboundEdges.length === 0 && outboundEdges.length === 0 && (
                <div style={{ color: '#9E9E9E', fontSize: 12, textAlign: 'center', padding: '24px 0' }}>
                  No connectors
                </div>
              )}
            </div>
          )}

          {/* KAFKA TAB */}
          {activeTab === 'kafka' && (
            <div>
              {kafkaPublish.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#E65100', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>↑</span> PUBLISH ({kafkaPublish.length})
                  </div>
                  {kafkaPublish.map(e => <KafkaQueueRow key={e.id} edge={e} nodes={nodes} role="publish" />)}
                </>
              )}
              {kafkaSubscribe.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#1565C0', margin: kafkaPublish.length > 0 ? '16px 0 8px' : '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>↓</span> SUBSCRIBE ({kafkaSubscribe.length})
                  </div>
                  {kafkaSubscribe.map(e => <KafkaQueueRow key={e.id} edge={e} nodes={nodes} role="subscribe" />)}
                </>
              )}
            </div>
          )}

          {/* FLOWS TAB */}
          {activeTab === 'flows' && (
            <div>
              {nodeFlows.length === 0 ? (
                <div style={{ color: '#9E9E9E', fontSize: 12, textAlign: 'center', padding: '24px 0' }}>
                  Not part of any flow
                </div>
              ) : (
                nodeFlows.map(f => (
                  <div
                    key={f.id}
                    style={{
                      padding: '10px 12px',
                      marginBottom: 8,
                      border: `1.5px solid ${f.color}`,
                      borderRadius: 8,
                      background: `${f.color}0D`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#212121' }}>{f.name}</span>
                      <span
                        style={{
                          fontSize: 10,
                          padding: '1px 6px',
                          borderRadius: 4,
                          background: '#E0E0E0',
                          color: '#616161',
                          marginLeft: 'auto',
                        }}
                      >
                        {f.type}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#757575' }}>{f.description}</div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* DOCS TAB */}
          {activeTab === 'docs' && svcData && (
            <div>
              {svcData.docFile ? (
                <div style={{ padding: 12, background: '#F5F5F5', borderRadius: 6, fontSize: 12 }}>
                  <div style={{ color: '#757575', marginBottom: 6 }}>Documentation file:</div>
                  <code style={{ color: '#1565C0' }}>{svcData.docFile}</code>
                </div>
              ) : (
                <div style={{ color: '#9E9E9E', fontSize: 12, textAlign: 'center', padding: '24px 0' }}>
                  No documentation linked
                </div>
              )}
            </div>
          )}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

const InfoRow: React.FC<{ label: string; value?: string; children?: React.ReactNode }> = ({
  label,
  value,
  children,
}) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: '#9E9E9E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: 13, color: '#212121' }}>{children ?? value}</div>
  </div>
);

const ConnectorRow: React.FC<{ edge: any; nodes: any[]; direction: 'in' | 'out' }> = ({
  edge,
  nodes,
  direction,
}) => {
  const data = edge.data as ConnectorData;
  const cs = CONNECTOR_STYLES[data?.type ?? 'REST'];
  const peerId = direction === 'in' ? edge.source : edge.target;
  const peer = nodes.find(n => n.id === peerId);
  const peerName = (peer?.data as any)?.name ?? peerId;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '6px 0',
        borderBottom: '1px solid #F0F0F0',
      }}
    >
      <span
        style={{
          fontSize: 10,
          color: '#BDBDBD',
          fontWeight: 700,
          minWidth: 26,
          paddingTop: 3,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        #{data?.index ?? '–'}
      </span>
      <span
        style={{
          padding: '2px 6px',
          borderRadius: 3,
          fontSize: 10,
          fontWeight: 700,
          background: cs.color,
          color: '#fff',
          marginTop: 1,
          flexShrink: 0,
        }}
      >
        {cs.label}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: '#424242', fontWeight: 600 }}>{peerName}</div>
        {data?.label && (
          <div style={{ fontSize: 11, color: '#757575', wordBreak: 'break-all' }}>{data.label}</div>
        )}
        {data?.slaMs && (
          <div style={{ fontSize: 10, color: '#9E9E9E' }}>SLA {data.slaMs}ms</div>
        )}
      </div>
    </div>
  );
};

const KafkaQueueRow: React.FC<{ edge: any; nodes: any[]; role: 'publish' | 'subscribe' }> = ({
  edge,
  nodes,
  role,
}) => {
  const data = edge.data as ConnectorData;
  const peerId = role === 'publish' ? edge.target : edge.source;
  const peer = nodes.find(n => n.id === peerId);
  const peerName = (peer?.data as any)?.name ?? peerId;
  const topicName = data?.eventName ?? data?.label ?? '—';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '8px 10px',
        marginBottom: 6,
        borderRadius: 6,
        background: role === 'publish' ? '#FFF3E0' : '#E3F2FD',
        border: `1px solid ${role === 'publish' ? '#FFB74D' : '#90CAF9'}`,
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: role === 'publish' ? '#F57C00' : '#1976D2',
          flexShrink: 0,
          marginTop: 3,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#212121',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={topicName}
        >
          {topicName}
        </div>
        <div style={{ fontSize: 11, color: '#616161', marginTop: 2 }}>
          {role === 'publish' ? '→' : '←'} {peerName}
        </div>
        {data?.flowIds?.length > 0 && (
          <div style={{ fontSize: 10, color: '#9E9E9E', marginTop: 2 }}>
            flows: {data.flowIds.join(', ')}
          </div>
        )}
      </div>
    </div>
  );
};

export default RightPanel;
